import type { Gateway } from '../../../../types';
import {
    canAccessGatewaySettingsSection,
    getGatewayAccess,
} from '../../gateway-access.utils';

const trimTrailingSlash = (pathname: string) => {
    if (pathname === '/') return pathname;

    return pathname.replace(/\/+$/, '');
};

const findGatewayForPath = (pathname: string, gateways: Gateway[]) => {
    return gateways.find((gateway) => {
        const gatewayRoot = `/${gateway.website}`;

        return (
            pathname === gatewayRoot || pathname.startsWith(`${gatewayRoot}/`)
        );
    });
};

export const getGatewayLandingPath = (
    gateway: Gateway,
    profileId?: number,
): string => {
    const gatewayRoot = `/${gateway.website}`;
    const access = getGatewayAccess(gateway, profileId);

    if (access.canEnterDashboard) {
        return gatewayRoot;
    }

    if (access.canViewTransactionAnalytics) {
        return `${gatewayRoot}/transaction-analytics`;
    }

    if (access.canViewCustomerBehaviorAnalytics) {
        return `${gatewayRoot}/customer-behavior-analytics`;
    }

    if (access.canExportRangeReport) {
        return `${gatewayRoot}/range-report`;
    }

    if (access.canManageOverallSettings) {
        return `${gatewayRoot}/settings/overall`;
    }

    if (access.canManageNotificationSettings) {
        return `${gatewayRoot}/settings/notifications`;
    }

    return gatewayRoot;
};

export const getGatewaySettingsLandingPath = (
    gateway: Gateway,
    profileId?: number,
): string => {
    const gatewayRoot = `/${gateway.website}`;
    const access = getGatewayAccess(gateway, profileId);

    if (access.canManageOverallSettings) {
        return `${gatewayRoot}/settings/overall`;
    }

    if (access.canManageNotificationSettings) {
        return `${gatewayRoot}/settings/notifications`;
    }

    return getGatewayLandingPath(gateway, profileId);
};

/**
 * Keeps the user in the same gateway-scoped section while changing gateway.
 * Detail ids belong to the previous gateway, so detail pages intentionally
 * fall back to their list page instead of carrying the old resource id over.
 * If the destination gateway does not grant access to the current section,
 * it falls back to that gateway's first accessible page.
 */
export const getGatewaySwitchPath = (
    pathname: string,
    gateways: Gateway[],
    nextGateway: Gateway,
    profileId?: number,
) => {
    const normalizedPath = trimTrailingSlash(pathname || '/');
    const nextGatewayRoot = `/${nextGateway.website}`;
    const landingPath = getGatewayLandingPath(nextGateway, profileId);
    const access = getGatewayAccess(nextGateway, profileId);

    if (normalizedPath === '/') {
        return landingPath;
    }

    const currentGateway = findGatewayForPath(normalizedPath, gateways);

    if (!currentGateway) {
        return normalizedPath;
    }

    if (currentGateway.id === nextGateway.id) {
        return normalizedPath;
    }

    const currentGatewayRoot = `/${currentGateway.website}`;

    if (normalizedPath === currentGatewayRoot) {
        return landingPath;
    }

    const relativePath = normalizedPath.slice(currentGatewayRoot.length);
    const relativeSegments = relativePath.split('/').filter(Boolean);
    const section = relativeSegments[0];

    if (section === 'transaction') {
        if (!access.canViewTransactions) {
            return landingPath;
        }

        return `${nextGatewayRoot}/transaction`;
    }

    if (section === 'withdraw') {
        if (!access.canViewWithdrawals) {
            return landingPath;
        }

        return `${nextGatewayRoot}/withdraw`;
    }

    if (section === 'transaction-analytics') {
        return access.canViewTransactionAnalytics
            ? `${nextGatewayRoot}/transaction-analytics`
            : landingPath;
    }

    if (section === 'customer-behavior-analytics') {
        return access.canViewCustomerBehaviorAnalytics
            ? `${nextGatewayRoot}/customer-behavior-analytics`
            : landingPath;
    }

    if (section === 'range-report') {
        return access.canExportRangeReport
            ? `${nextGatewayRoot}/range-report`
            : landingPath;
    }

    if (section === 'settings') {
        const settingsSection = relativeSegments[1];

        return canAccessGatewaySettingsSection(access, settingsSection)
            ? `${nextGatewayRoot}${relativePath}`
            : landingPath;
    }

    return `${nextGatewayRoot}${relativePath}`;
};
